import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Save, DollarSign, Image as ImageIcon, FileText, FileArchive, Eye, EyeOff, Trash2 } from "lucide-react";
import { Tables } from "@/integrations/supabase/types";
import { ImageUpload, VehicleImages } from "@/components/admin/ImageUpload";
import { VehicleExpenses } from "@/components/admin/VehicleExpenses";
import { VehicleFinancialSummary } from "@/components/admin/VehicleFinancialSummary";
import { VehicleDocuments } from "@/components/admin/VehicleDocuments";
import { ConfirmDialog } from "@/components/admin/ConfirmDialog";
import { logAudit, computeChanges } from "@/utils/auditLog";
import { useAuth } from "@/hooks/useAuth";
import { useKeyboardShortcut } from "@/hooks/useKeyboardShortcut";

type Vehicle = Tables<"vehicles">;
type VehicleImage = Tables<"vehicle_images">;
type VehicleExpense = Tables<"vehicle_expenses">;

export default function VehicleDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentTab = searchParams.get("tab") || "details";
  const [vehicle, setVehicle] = useState<Vehicle | null>(null);
  const [images, setImages] = useState<VehicleImage[]>([]);
  const [expenses, setExpenses] = useState<VehicleExpense[]>([]);
  const [revenue, setRevenue] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    make: "", model: "", year: new Date().getFullYear(),
    price: "", mileage: "", color: "", interior_color: "",
    vin: "", transmission: "automatic", fuel_type: "gasoline",
    drivetrain: "fwd", description: "",
    status: "available" as "available" | "sold" | "pending",
    featured: false, carfax_url: "",
    purchase_cost: "", purchase_hst: "", purchase_date: "",
    is_hidden: false,
    body_type: "", trim: "", stock_number: "",
  });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (id) loadVehicle(id);
  }, [id]);

  const loadVehicle = async (vehicleId: string) => {
    setIsLoading(true);
    const [vehicleRes, imagesRes, expensesRes, revenueRes] = await Promise.all([
      supabase.from("vehicles").select("*").eq("id", vehicleId).single(),
      supabase.from("vehicle_images").select("*").eq("vehicle_id", vehicleId)
        .order("display_order", { ascending: true }).order("is_primary", { ascending: false }),
      supabase.from("vehicle_expenses").select("*").eq("vehicle_id", vehicleId)
        .is("deleted_at", null).order("expense_date", { ascending: false }),
      supabase.from("vehicle_sales_revenue").select("*").eq("vehicle_id", vehicleId)
        .is("deleted_at", null).order("revenue_date", { ascending: false }),
    ]);

    if (vehicleRes.error || !vehicleRes.data) {
      toast.error("Vehicle not found");
      navigate("/admin/inventory");
      return;
    }

    const v = vehicleRes.data;
    setVehicle(v);
    setImages(imagesRes.data || []);
    setExpenses(expensesRes.data || []);
    setRevenue(revenueRes.data || []);
    setFormData({
      make: v.make, model: v.model, year: v.year,
      price: v.price.toString(), mileage: v.mileage.toString(),
      color: v.color, interior_color: v.interior_color || "",
      vin: v.vin, transmission: v.transmission, fuel_type: v.fuel_type,
      drivetrain: v.drivetrain || "fwd", description: v.description || "",
      status: v.status, featured: v.featured, carfax_url: v.carfax_url || "",
      purchase_cost: v.purchase_cost?.toString() || "",
      purchase_hst: v.purchase_hst?.toString() || "",
      purchase_date: v.purchase_date || "",
      is_hidden: v.is_hidden || false,
      body_type: v.body_type || "",
      trim: v.trim || "",
      stock_number: v.stock_number || "",
    });
    setIsLoading(false);
  };

  const loadImages = async () => {
    if (!id) return;
    const { data } = await supabase.from("vehicle_images").select("*")
      .eq("vehicle_id", id).order("display_order").order("is_primary", { ascending: false });
    setImages(data || []);
  };

  const loadExpenses = async () => {
    if (!id) return;
    const { data } = await supabase.from("vehicle_expenses").select("*")
      .eq("vehicle_id", id).is("deleted_at", null).order("expense_date", { ascending: false });
    setExpenses(data || []);
  };

  const loadRevenue = async () => {
    if (!id) return;
    const { data } = await supabase.from("vehicle_sales_revenue").select("*")
      .eq("vehicle_id", id).is("deleted_at", null).order("revenue_date", { ascending: false });
    setRevenue(data || []);
  };

  const handleSave = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!id || !vehicle) return;

    setIsSaving(true);
    const updatedData = {
      make: formData.make,
      model: formData.model,
      year: formData.year,
      price: parseFloat(formData.price) || 0,
      mileage: parseInt(formData.mileage) || 0,
      color: formData.color,
      interior_color: formData.interior_color || null,
      vin: formData.vin,
      transmission: formData.transmission,
      fuel_type: formData.fuel_type,
      drivetrain: formData.drivetrain,
      description: formData.description || null,
      status: formData.status,
      featured: formData.featured,
      carfax_url: formData.carfax_url || null,
      purchase_cost: formData.purchase_cost ? parseFloat(formData.purchase_cost) : null,
      purchase_hst: formData.purchase_hst ? parseFloat(formData.purchase_hst) : null,
      purchase_date: formData.purchase_date || null,
      is_hidden: formData.is_hidden,
      body_type: formData.body_type || null,
      trim: formData.trim || null,
      stock_number: formData.stock_number || null,
    };

    const { error } = await supabase.from("vehicles").update(updatedData).eq("id", id);

    if (error) {
      toast.error("Failed to save");
    } else {
      toast.success("Vehicle updated");
      const changes = computeChanges(
        vehicle as unknown as Record<string, unknown>,
        updatedData as unknown as Record<string, unknown>,
        [
          'make', 'model', 'year', 'price', 'mileage', 'color', 'interior_color',
          'vin', 'transmission', 'fuel_type', 'drivetrain', 'status', 'featured',
          'purchase_cost', 'purchase_hst', 'purchase_date', 'is_hidden',
          'body_type', 'trim', 'stock_number'
        ]
      );
      await logAudit({
        entityType: 'vehicle',
        entityId: id,
        action: 'updated',
        changes: Object.keys(changes).length > 0 ? changes : undefined,
        metadata: { name: `${formData.year} ${formData.make} ${formData.model}`, vin: formData.vin },
        performedBy: user?.id,
      });
      loadVehicle(id);
    }
    setIsSaving(false);
  };

  useKeyboardShortcut(["ctrl", "s"], () => handleSave());

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!vehicle) return null;

  const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;

  const toggleHidden = async () => {
    const newHidden = !formData.is_hidden;
    const { error } = await supabase.from("vehicles").update({ is_hidden: newHidden }).eq("id", id!);
    if (error) {
      toast.error("Failed to update visibility");
    } else {
      setFormData(prev => ({ ...prev, is_hidden: newHidden }));
      toast.success(newHidden ? "Vehicle hidden from website" : "Vehicle visible on website");
      await logAudit({
        entityType: 'vehicle',
        entityId: id!,
        action: newHidden ? 'hidden' : 'unhidden',
        metadata: { name: vehicleName },
        performedBy: user?.id,
      });
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const { error } = await supabase.rpc('soft_delete_vehicle', {
      p_vehicle_id: id,
      p_deleted_by: user?.id || '',
    });
    if (error) {
      toast.error('Failed to delete vehicle: ' + error.message);
    } else {
      toast.success('Vehicle moved to recycle bin');
      navigate('/admin/inventory');
    }
    setShowDeleteConfirm(false);
  };

  const handleTabChange = (value: string) => {
    setSearchParams({ tab: value }, { replace: true });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/inventory")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold">{vehicleName}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={vehicle.status === "available" ? "default" : vehicle.status === "sold" ? "secondary" : "outline"}>
                {vehicle.status}
              </Badge>
              {formData.is_hidden && (
                <Badge variant="outline" className="border-amber-400 text-amber-600 bg-amber-50">Hidden from website</Badge>
              )}
              <span className="text-sm text-muted-foreground">VIN: {vehicle.vin}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant={formData.is_hidden ? "default" : "outline"}
            size="sm"
            onClick={toggleHidden}
            className={formData.is_hidden ? "bg-amber-500 hover:bg-amber-600 text-white" : ""}
          >
            {formData.is_hidden ? (
              <><Eye className="h-4 w-4 mr-2" /> Show on Website</>
            ) : (
              <><EyeOff className="h-4 w-4 mr-2" /> Hide from Website</>
            )}
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="text-destructive hover:bg-destructive/10 border-destructive/30"
            onClick={() => setShowDeleteConfirm(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" /> Delete
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={currentTab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="details" className="flex items-center gap-1">
            <FileText className="h-4 w-4" /> Details
          </TabsTrigger>
          <TabsTrigger value="images" className="flex items-center gap-1">
            <ImageIcon className="h-4 w-4" /> Images
          </TabsTrigger>
          <TabsTrigger value="finances" className="flex items-center gap-1">
            <DollarSign className="h-4 w-4" /> Finances
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-1">
            <FileArchive className="h-4 w-4" /> Documents
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="mt-4">
        {/* Details Tab */}
        <div className={currentTab === "details" ? "block" : "hidden"}>
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSave} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label>Make *</Label>
                    <Input required value={formData.make} onChange={(e) => setFormData({ ...formData, make: e.target.value })} />
                  </div>
                  <div>
                    <Label>Model *</Label>
                    <Input required value={formData.model} onChange={(e) => setFormData({ ...formData, model: e.target.value })} />
                  </div>
                  <div>
                    <Label>Year *</Label>
                    <Input type="number" required value={formData.year} onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })} />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Listing Price *</Label>
                    <Input type="number" required value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
                  </div>
                  <div>
                    <Label>Mileage *</Label>
                    <Input type="number" required value={formData.mileage} onChange={(e) => setFormData({ ...formData, mileage: e.target.value })} />
                  </div>
                  <div>
                    <Label>Exterior Color *</Label>
                    <Input required value={formData.color} onChange={(e) => setFormData({ ...formData, color: e.target.value })} />
                  </div>
                  <div>
                    <Label>Interior Color</Label>
                    <Input value={formData.interior_color} onChange={(e) => setFormData({ ...formData, interior_color: e.target.value })} />
                  </div>
                  <div>
                    <Label>Stock Number</Label>
                    <Input value={formData.stock_number} onChange={(e) => setFormData({ ...formData, stock_number: e.target.value })} />
                  </div>
                  <div>
                    <Label>Body Type</Label>
                    <Input placeholder="e.g. Sedan, SUV" value={formData.body_type} onChange={(e) => setFormData({ ...formData, body_type: e.target.value })} />
                  </div>
                  <div>
                    <Label>Trim Level</Label>
                    <Input placeholder="e.g. SE, LE" value={formData.trim} onChange={(e) => setFormData({ ...formData, trim: e.target.value })} />
                  </div>
                  <div>
                    <Label>VIN *</Label>
                    <Input required value={formData.vin} onChange={(e) => setFormData({ ...formData, vin: e.target.value })} />
                  </div>
                  <div>
                    <Label>Transmission</Label>
                    <Select value={formData.transmission} onValueChange={(v) => setFormData({ ...formData, transmission: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="automatic">Automatic</SelectItem>
                        <SelectItem value="manual">Manual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Fuel Type</Label>
                    <Select value={formData.fuel_type} onValueChange={(v) => setFormData({ ...formData, fuel_type: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="gasoline">Gasoline</SelectItem>
                        <SelectItem value="diesel">Diesel</SelectItem>
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="hybrid">Hybrid</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Drivetrain</Label>
                    <Select value={formData.drivetrain} onValueChange={(v) => setFormData({ ...formData, drivetrain: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="fwd">FWD</SelectItem>
                        <SelectItem value="rwd">RWD</SelectItem>
                        <SelectItem value="awd">AWD</SelectItem>
                        <SelectItem value="4wd">4WD</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v as any })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="sold">Sold</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Carfax URL</Label>
                  <Input placeholder="https://www.carfax.ca/..." value={formData.carfax_url}
                    onChange={(e) => setFormData({ ...formData, carfax_url: e.target.value })} />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea value={formData.description} rows={4}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox id="featured" checked={formData.featured}
                    onCheckedChange={(checked) => setFormData({ ...formData, featured: checked as boolean })} />
                  <Label htmlFor="featured" className="font-normal cursor-pointer">Featured Vehicle</Label>
                </div>
                <div className="flex justify-end">
                  <Button type="submit" disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Save Changes
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>

        {/* Images Tab */}
        <div className={currentTab === "images" ? "space-y-4 block" : "hidden"}>
          <ImageUpload vehicleId={vehicle.id} onImagesUploaded={loadImages} />
          <VehicleImages vehicleId={vehicle.id} images={images} onImagesChanged={loadImages} />
        </div>

        {/* Finances Tab */}
        <div className={currentTab === "finances" ? "space-y-6 block" : "hidden"}>
          <VehicleFinancialSummary vehicle={vehicle} expenses={expenses} revenue={revenue} onVehicleUpdated={() => loadVehicle(id!)} />
          
          <Card>
            <CardContent className="p-6">
              <form onSubmit={handleSave} className="space-y-4">
                <h3 className="font-semibold text-lg flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-primary" /> Purchase Information
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <Label>Purchase Cost (excl. HST)</Label>
                    <Input type="number" step="0.01" placeholder="0.00"
                      value={formData.purchase_cost}
                      onChange={(e) => setFormData({ ...formData, purchase_cost: e.target.value })} />
                  </div>
                  <div>
                    <Label>HST Paid on Purchase</Label>
                    <Input type="number" step="0.01" placeholder="0.00"
                      value={formData.purchase_hst}
                      onChange={(e) => setFormData({ ...formData, purchase_hst: e.target.value })} />
                  </div>
                  <div>
                    <Label>Purchase Date</Label>
                    <Input type="date" value={formData.purchase_date}
                      onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })} />
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <Button type="submit" size="sm" disabled={isSaving}>
                    {isSaving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    Update Purchase Info
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          <VehicleExpenses vehicleId={vehicle.id} vehicleName={vehicleName} onExpensesChanged={loadExpenses} />
        </div>

        {/* Documents Tab */}
        <div className={currentTab === "documents" ? "block" : "hidden"}>
          <VehicleDocuments vehicleId={vehicle.id} />
        </div>
      </div>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        onConfirm={handleDelete}
        title="Delete Vehicle"
        description={`Are you sure you want to delete "${vehicleName}"? The vehicle and all related data will be moved to the recycle bin where it can be restored later.`}
        confirmLabel="Move to Recycle Bin"
        variant="destructive"
      />
    </div>
  );
}
